import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { TerminationRequest } from "../../model/termination-request";
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { TerminationService } from 'src/app/services/termination/termination.service';
import { ColumnDef } from 'src/app/model/column-definition';
import { MatPaginator } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/constants/app-constants';
import { Subscription } from 'rxjs';
import { DialogService } from 'src/app/services/dialog/dialog.service';

/**
 * TerminationPageComponent represents the 'termination' menu
 */
@Component({
  selector: 'app-termination-page',
  templateUrl: './termination-page.component.html',
  styleUrls: ['./termination-page.component.scss']
})
export class TerminationPageComponent implements OnInit, AfterViewInit {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, { static: true }) sort: MatSort = new MatSort();

  /** The table of terminations */
  @ViewChild(MatTable) table!: MatTable<TerminationRequest>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<TerminationRequest>([]);

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new terminations */
  private _sub: Subscription | null = null;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    { header: "ID", field: "id" },
    { header: "Status", field: "status", format: "status" },
    { header: "Created At", field: "createdAt", format: "date", toStringTransform: (x) => new Date(x).toDateString() },
    { header: "Created By", field: "createdByDisplay" },
    { header: "Actions", field: "actions", format: "actions" },
  ]
  public displayedCols: string[] = this.colDefs.map(x => x.field);

  /**
   * Constructor for the terminations page to initialize services and set a base snapshot 
   * 
   * @param _terminationService 
   * @param _router
   * @param _dialogService
   */
  constructor(
    private _terminationService: TerminationService,
    private _router: Router,
    private _dialogService: DialogService,
  ) {
    // Subscribe to timer changes
    this._terminationService.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });
  }

  /**
   * Initialize the sort, paginator, and termination data source
   */
  ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimiters
    this.source.filterPredicate = (data: TerminationRequest, filter: string): boolean => {
      return data.toString(this.colDefs).includes(filter);
    }

    // Get the terminations from the service
    this._sub = this._terminationService.terminations$.subscribe(
      (resp: TerminationRequest[]) => {
        this._populateSource(resp);
      });

    this._populateSource(this._terminationService.getTerminations());
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: TerminationRequest, property: string): string | number => {
      return (item as any)[property];
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._sub?.unsubscribe();
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Open the approve termination dialog for the given termination record
   * 
   * @param terminationRecord The termination record to provide to the dialog
   */
  public approveTermination(terminationRecord: TerminationRequest): void {
    this._dialogService.openApproveTerminationDialog(terminationRecord).subscribe(
      (result: string | null) => {
        if (result) {
          console.log(`[TERMINATION-PAGE]: Termination ${terminationRecord.id} was ${result}`);
        }
      }
    );
  }

  /**
   * Open the view termination dialog for the given termination record
   * 
   * @param terminationRecord The termination record to provide to the dialog
   */
  public viewTermination(terminationRecord: TerminationRequest): void {
    this._dialogService.openViewTerminationDialog(terminationRecord).subscribe({
      next: (result: string | null) => {
        if (result) {
          console.log(`[TERMINATION-PAGE]: Termination ${terminationRecord.id} was viewed`);
        }
      }
    });
  }

  /**
   * Populate the data source meticulously so it doesn't lag the application
   * Note: I don't know if async is required, I'm just paranoid
   */
  private async _populateSource(incoming: TerminationRequest[]): Promise<void> {
    // Don't update the table when the user is not on the page => angular will be mad :(
    // TODO: can add this clause to updating the source data. Can then add notifications to some future service from changes found here
    if (this._router.url.replace("/", "") !== AppConstants.MENU_TERMINATIONS) return;

    let source = this.source.data;
    const current = this.source.data.map((x: TerminationRequest) => x.copyOf());

    // For each incoming termination, determine if it exists in the source. 
    const update = async () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If termination IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the terminations are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf();
            }

            // We've updated the termination, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    await update();

    // Add new terminations that we don't currently have
    const add = async () => {
      for (let newTermination of incoming) {
        source.push(newTermination);
      }
    }
    await add();

    // Remove old terminations that have been removed
    const remove = async () => {
      for (let oldTermination of current) {
        source = source.filter(x => x.id !== oldTermination.id);
      }
    }
    await remove();

    // Update the table
    this.source.data = source;
  }
}
